"use client";

import { useEffect, useState } from "react";
import { Copy, Check, Eye, Tag } from "lucide-react";
import { toast } from "sonner";
import { useTranslations, useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HorizontalScrollRow } from "@/components/horizontal-scroll-row";
import { useDiscountCodesByShop } from "@/hooks/queries";
import {
  APP_LOCAL_STORAGE_REOPEN_CODE_DIALOG,
  APP_LOCAL_STORAGE_REOPEN_CODE_VALUE,
  APP_SESSION_STORAGE_ANON_REVEAL,
} from "@/lib/constants";
import { useAppContext } from "@/contexts/app-context";
import { formatDate } from "@donista/shared/utils/format-date";
import { maskCode } from "@/lib/utils/mask-code";
import { CodeCopiedDialog } from "./code-copied-dialog";

interface ShopDiscountCodesScrollProps {
  shopId: string;
  shopName: string;
  websiteUrl: string;
}

function CopyCodeButton({ code, onCopied }: { code: string; onCopied: () => void }) {
  const t = useTranslations("discountCodes");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      toast.error(t("copyFailed"));
      return;
    }
    setCopied(true);
    onCopied();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 shrink-0">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? t("copied") : t("copyCode")}
    </Button>
  );
}

export function ShopDiscountCodesScroll({ shopId, shopName, websiteUrl }: ShopDiscountCodesScrollProps) {
  const t = useTranslations("discountCodes");
  const locale = useLocale();
  const { user } = useAppContext();
  // NOTE: keep this a CLIENT-ONLY query — never SSR-prefetch it into a
  // HydrationBoundary. The masking below only protects the rendered DOM; if the
  // codes landed in the dehydrated HTML they'd be crawlable and the gate defeated.
  const { data: codes } = useDiscountCodesByShop(shopId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogView, setDialogView] = useState<"gate" | "reveal">("gate");
  const [needsCopy, setNeedsCopy] = useState(true);
  const [selectedCode, setSelectedCode] = useState("");
  // Anonymous users who chose "Anonym fortfahren" reveal real codes for the rest
  // of the session. Kept as reactive state (not a mount-only read) so choosing it
  // unmasks every code on the page instantly, without a manual refresh.
  const [anonRevealed, setAnonRevealed] = useState(false);

  // Logged-in users, or anon users who already continued this session, see real
  // codes; everyone else sees the masked form until they pass the reveal gate.
  const revealed = !!user || anonRevealed;

  // Mount-time restore: (a) session anon-reveal flag; (b) reopen the reveal dialog
  // after an email magic-link round-trip (signin-form persists a path flag + the
  // selected code keyed to the page check-mail returns to). Safe to read
  // storage here, never during render.
  useEffect(() => {
    if (sessionStorage.getItem(APP_SESSION_STORAGE_ANON_REVEAL) === "1") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot: reflect persisted session-reveal into UI on mount
      setAnonRevealed(true);
    }
    if (localStorage.getItem(APP_LOCAL_STORAGE_REOPEN_CODE_DIALOG) === window.location.pathname) {
      const reopenCode = localStorage.getItem(APP_LOCAL_STORAGE_REOPEN_CODE_VALUE) ?? "";
      localStorage.removeItem(APP_LOCAL_STORAGE_REOPEN_CODE_DIALOG);
      localStorage.removeItem(APP_LOCAL_STORAGE_REOPEN_CODE_VALUE);
      // User returns logged-in but never copied → land on reveal with the copy CTA.
      setSelectedCode(reopenCode);
      setDialogView("reveal");
      setNeedsCopy(true);
      setDialogOpen(true);
    }
  }, []);

  const openGate = (code: string) => {
    setSelectedCode(code);
    setDialogView("gate");
    setNeedsCopy(true); // the eventual reveal was never copied
    setDialogOpen(true);
  };

  const openRevealAfterCopy = (code: string) => {
    setSelectedCode(code);
    setDialogView("reveal");
    setNeedsCopy(false); // CopyCodeButton already wrote to the clipboard
    setDialogOpen(true);
  };

  const handleContinueAnonymously = async () => {
    // Restore pre-funnel behavior: continuing anonymously provisions a Firebase
    // anonymous user so clicks/donations are attributed. Dynamic import keeps the
    // Firebase bundle out of every shop page (mirrors the SigninForm lazy import
    // in code-copied-dialog.tsx) — loaded only when the user actually continues.
    // Guard on !user: signInAnonymously() replaces auth.currentUser, so calling it
    // while logged in would swap a real session for a fresh anon one and trigger a
    // destructive reload via onIdTokenChanged. The gate is UI-unreachable when
    // logged in today, but the guard keeps that safe against future refactors.
    // Errors are intentionally left to propagate — the caller (CodeCopiedDialog)
    // needs to know the sign-in failed so it doesn't reveal the real code.
    if (!user) {
      const { signInWithAnon } = await import("@/app/[locale]/(auth)/signin/auth");
      await signInWithAnon();
    }
    setAnonRevealed(true);
    sessionStorage.setItem(APP_SESSION_STORAGE_ANON_REVEAL, "1");
  };

  const hasCodes = !!codes && codes.length > 0;

  // The dialog must render regardless of `codes` (it can reopen after a sign-in
  // round-trip while codes are still loading / evicted from cache), so we render
  // a fragment and gate only the codes list on `hasCodes` instead of early-returning.
  return (
    <>
      {hasCodes && (
        <div className="text-left">
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5" />
            {t("title")}
          </h2>
          <HorizontalScrollRow className="items-stretch">
            {codes.map((code: NonNullable<typeof codes>[number], index: number) => {
          const isExpired = code.status === "EXPIRED";
          const codeValue = code.code;

          return (
            <div
              key={code.id ?? code.code ?? String(index)}
              className={`shrink-0 w-64 sm:w-72 rounded-xl border p-4 flex flex-col gap-2 bg-black/5 dark:bg-black/30 backdrop-blur-md ${isExpired ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                {code.title && (
                  <span className="font-semibold text-sm line-clamp-1">{code.title}</span>
                )}
                <div className="flex gap-1.5 shrink-0">
                  {code.expiringSoon && (
                    <Badge variant="destructive" className="text-xs">{t("expiringSoon")}</Badge>
                  )}
                  {code.exclusive && (
                    <Badge className="text-xs">{t("exclusive")}</Badge>
                  )}
                </div>
              </div>

              {code.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{code.description}</p>
              )}

              {(code.discountAmount != null || code.discountPercentage != null) && (
                <span className="text-lg font-bold text-primary block">
                  {code.discountPercentage != null
                    ? `-${code.discountPercentage}%`
                    : `−${(code.discountAmount ?? 0).toLocaleString("de-DE", { style: "currency", currency: code.currencyCode ?? "EUR" })}`}
                </span>
              )}

              <div className="mt-auto flex flex-col gap-2">
                {codeValue && (
                  <div className="flex items-center gap-2 bg-muted rounded-lg border px-3 py-2">
                    <code className="text-sm font-mono font-bold tracking-wider flex-1 truncate">
                      {revealed ? codeValue : maskCode(codeValue)}
                    </code>
                    {revealed ? (
                      <CopyCodeButton code={codeValue} onCopied={() => openRevealAfterCopy(codeValue)} />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openGate(codeValue)}
                        className="gap-1.5 shrink-0"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {t("showCode")}
                      </Button>
                    )}
                  </div>
                )}

                {code.endsAt && (
                  <p className={`text-xs ${isExpired ? "text-destructive" : "text-muted-foreground"}`}>
                    {t("validUntil", { date: formatDate(code.endsAt, locale) })}
                  </p>
                )}
              </div>
            </div>
              );
            })}
          </HorizontalScrollRow>
        </div>
      )}
      <CodeCopiedDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        shopName={shopName}
        websiteUrl={websiteUrl}
        selectedCode={selectedCode}
        initialView={dialogView}
        needsCopy={needsCopy}
        onContinueAnonymously={handleContinueAnonymously}
      />
    </>
  );
}
